import React, { useState, useMemo, useEffect, useContext, createContext} from 'react';
import './ApprovalsStyles.css';
import { FaCheck, FaTimes, FaRedo, FaChevronDown, FaSearch, FaEye } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from 'react-router-dom';  // added useLocation
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useTimesheetDate } from './context/DateContext.js';
import { confirm } from './confirmWindow/confirm.js';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

//status mapping
const statusMap = {
  1: "draft",
  2: "pending",
  3: "approved",
  4: "rejected",
};

const TimesheetApprovals = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);

  const [timeSheets, setTimeSheets] = useState([]);
  const [filteredSheets, setFilteredSheets] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInputs, setSearchInputs] = useState({});
  const [searchVisible, setSearchVisible] = useState({});
  const rowsPerPage = 5;

  const [tsApiError, setTsApiError] = useState(false);

  const { ApprTsStartDate, 
          ApprTsSetStartDate, 
          ApprTsEndDate, 
          ApprTsSetEndDate, 
          ApprTsHasFetched, 
          ApprTsSetHasFetched } = useTimesheetDate();

  const fetchTimeSheets = async (from = "", to = "") => {
    if (!token || !user) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}timesheet/all?userId=&projectId=&from=${from}&to=${to}&orgId=${user.department?.costCenter?.organization?.orgId}&approverId=${user.userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const apiData = response.data || [];
      console.log("Timesheet API response:", apiData);

      const mapped = apiData.map(ts => ({
        id: ts.tsId,
        status: statusMap[ts.status] || "unknown",
        employee: `${ts.user?.firstName || ""} ${ts.user?.lastName || ""}`.trim() || "-",
        title: ts.user.title.titleName || "-",
        fromDate: ts.dateFrom,
        toDate: ts.dateTo,
        project: ts.user?.projects?.[0]?.projectCode || "-",
        standardTime: ts.stSubtotal || 0,
        overtime: ts.otSubtotal || 0,
        total: ts.total || 0,
      }));
      

      setTimeSheets(mapped);
      setFilteredSheets(null);
      ApprTsSetHasFetched(true);
    } catch (error) {
      console.error("Error fetching timesheets:", error);
      setTsApiError(true);
      setTimeout(() => setTsApiError(false), 1500);
    }
  }; 

  const sortedTimeSheets = useMemo(() => {
    let sortableItems = filteredSheets ? [...filteredSheets] : [...timeSheets];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'approver') {
          aValue = aValue || '';
          bValue = bValue || '';
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [timeSheets, filteredSheets, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedTimeSheets.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedTimeSheets.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
// Not sorted: show both up and down faded
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    // Sorted: show only one arrow, centered, rotate if ascending
    const isAsc = sortConfig.direction === 'ascending';
    return (
      <FaChevronDown
        className={`table-sort-arrow centered${isAsc ? ' up' : ''}`}
      />
    );
  };
  
  const handleApplyFilter = () => {
    const from = ApprTsStartDate ? ApprTsStartDate.toISOString().split("T")[0] : "";
    const to = ApprTsEndDate ? ApprTsEndDate.toISOString().split("T")[0] : "";
    fetchTimeSheets(from, to);
  };

  useEffect(() => {
    if (ApprTsHasFetched) {
      handleApplyFilter();
    }
  }, []);
  
  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => {
      // Close all popovers first, then open the clicked one
      const allClosed = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      
      // If the clicked popover was already open, keep it closed
      // If it was closed, open it
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = searchInputs[key] || '';
    setFilteredSheets(
      timeSheets.filter(sheet =>
        String(sheet[key] || '').toLowerCase().includes(value.toLowerCase())
      )
    );
    setCurrentPage(1);
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  // Actions
  // Approve button handler (asks for confirmation)
  const handleApprove = (id) => {
    if (!token || !user) return;
    confirm.open({
      title: t("approvals.approveConfTitle"),
      message: t("approvals.approveTsConfMsg"),
      onConfirm: async () => {
        try {
          await axios.put(
            `${API_BASE_URL}timesheet/status?tsId=${id}&status=3`,
            null,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          handleApplyFilter();
        } catch (error) {
          console.error("Error approving timesheet " + id + ":", error);
          setTsApiError(true);
          setTimeout(() => setTsApiError(false), 5000);
        }
      },
      onCancel: () => {}
    });
  };


  // Reject button handler (asks for confirmation)
  const handleReject = (id) => {
    if (!token || !user) return;
    confirm.open({
      title: t("approvals.rejectConfTitle"),
      message: t("approvals.rejectTsConfMsg"),
      onConfirm: async () => {
        try {
          await axios.put(
            `${API_BASE_URL}timesheet/status?tsId=${id}&status=4`,
            null,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          handleApplyFilter();
        } catch (error) {
          console.error("Error rejecting timesheet " + id + ":", error);
          setTsApiError(true);
          setTimeout(() => setTsApiError(false), 5000);
        }
      },
      onCancel: () => {}
    });
  };


  // View button handler
  const handleView = (id) => {
    navigate(`/approvals/timesheetapprovals/view/${id}`);
  };

  // Render sortable header with search
  const renderSortableHeader = (key, titleKey) => (
    <th className="table-header sortable">
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span onClick={() => requestSort(key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          {t(`approvals.${titleKey}`)} {getSortIcon(key)}
        </span>
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={e => handleSearchInputChange(key, e.target.value)}
                onKeyDown={e => handleSearchInputKeyDown(key, e)}
                placeholder={t('approvals.searchPlaceholder', { field: t(`approvals.${titleKey}`) })}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px'}} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px'}} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px'}}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  return (
    <div className="approvals-container">
      {/* Header */}
      <div className="approvals-header-container" >
        <div className="approvals-header" >
          <h1 className="approvals-title">{t("approvals.timesheetApprovals")}</h1>
        </div>
        <nav className="approvals-tab-container">
          <nav className="approvals-tab-list">
            <NavLink
              key="/approvals/timesheetapprovals"
              to="/approvals/timesheetapprovals"
              className={({ isActive }) => 
                  `approvals-tab-item ${isActive ? 'active' : ''}`
                }
            >
              {t("approvals.timesheetApprovals")}
            </NavLink>
            <NavLink
              key="/approvals/expenseapprovals"
              to="/approvals/expenseapprovals"
              className={({ isActive }) => 
                  `approvals-tab-item ${isActive ? 'active' : ''}`
                }
            >
              {t("approvals.expenseApprovals")}
            </NavLink>
          </nav>
        </nav>
        {tsApiError && <h3 className="api-error-text">{t("approvals.apiFailure")}</h3>}
      </div>

      {/* Filters */}
      <div className="filter-wrapper">
        {/* From-To Date Filter */}
        <div className="date-filter-container">
          <div className="date-filter-controls">
            <div className="date-filter-group">
              <label className="ts-appr-filter-label">{t("approvals.from")}</label>
              <div className="date-picker-container">
                <DatePicker
                  selected={ApprTsStartDate}
                  onChange={date => ApprTsSetStartDate(date)}
                  selectsStart
                  startDate={ApprTsStartDate}
                  endDate={ApprTsEndDate}
                  placeholderText={t("approvals.selectStartDate")}
                  className="date-input"
                />
              </div>
            </div>
            <div className="date-filter-group">
              <label className="ts-appr-filter-label">{t("approvals.to")}</label>
              <div className="date-picker-container">
                <DatePicker
                  selected={ApprTsEndDate}
                  onChange={date => ApprTsSetEndDate(date)}
                  selectsEnd
                  startDate={ApprTsStartDate}
                  endDate={ApprTsEndDate}
                  minDate={ApprTsStartDate}
                  placeholderText={t("approvals.selectEndDate")}
                  className="date-input"
                />
                </div>  
              </div>
          </div>
        <button className="apply-filter-button" onClick= {() => handleApplyFilter()}>
          {t("approvals.search")}
        </button>
        </div>
      </div>
      
      {/* Table */}
      <div className="table-container">
        <table className="timesheets-table">
          <thead>
            <tr>
              {renderSortableHeader('status', 'status')}
              {renderSortableHeader('id', 'id')}
              {renderSortableHeader('employee', 'employee')}
              {renderSortableHeader('title', 'title')}
              {renderSortableHeader('fromDate', 'from')}
              {renderSortableHeader('toDate', 'to')}
              {renderSortableHeader('project', 'project')}
              {renderSortableHeader('StandardTime', 'st')}
              {renderSortableHeader('Overtime', 'ot')}
              {renderSortableHeader('Total', 'total')}
              <th className="table-header">{t("approvals.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((sheet) => (
              <tr key={sheet.id}>
                <td className="table-cell">
                  <span className={`status-badge ${sheet.status}`}>
                    {t(`approvals.${sheet.status}`, sheet.status.charAt(0).toUpperCase() + sheet.status.slice(1))}
                  </span>
                </td>
                <td className="table-cell">{sheet.id}</td>
                <td className="table-cell">{sheet.employee}</td>
                <td className="table-cell">{sheet.title}</td>
                <td className="table-cell">{sheet.fromDate}</td>
                <td className="table-cell">{sheet.toDate}</td>
                <td className="table-cell">{sheet.project}</td>
                <td className="table-cell">{sheet.standardTime}</td>
                <td className="table-cell">{sheet.overtime}</td>
                <td className="table-cell">{sheet.total}</td>
                <td className="table-cell">
                  <button className='action-button view' onClick={() => handleView(sheet.id)}>
                  <FaEye /> 
                    {t("approvals.view")}
                  </button>
                  {(sheet.status === 'pending') && (
                    <button className='action-button approve' onClick={() => tsApiError ? null : handleApprove(sheet.id)}>
                      <FaCheck /> 
                     {t("approvals.approve")}
                    </button>
                  )}
                  {(sheet.status === 'pending' || sheet.status === 'approved') && (
                    <button className="action-button reject" onClick={() => tsApiError ? null : handleReject(sheet.id)}>
                      <FaTimes /> 
                      {t("approvals.reject")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <button 
          onClick={handlePreviousPage} 
          disabled={currentPage === 1}
          className="pagination-arrow"
          aria-label={t("approvals.previous")}
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={e => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">
          {t("approvals.of")} {totalPages}
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={currentPage === totalPages}
          className="pagination-arrow"
          aria-label={t("approvals.next")}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default TimesheetApprovals;